import React from 'react'
import { Controller, RegisterOptions } from 'react-hook-form'
import { ReactComponent as EyeClosed } from './../../images/icons/eye_closed.svg'
import { ReactComponent as EyeOpen } from './../../images/icons/eye_open.svg'
import create from 'zustand'
import classNames from 'classnames'
interface IInput {
  type?: string
  placeholder?: string
  name: string
  label: string
  defaultValue?: string
  error?: any
  control?: any
  inverted?: boolean
  rules?: RegisterOptions
  info?: string
  onChange?: (value: string) => string | undefined
  disabled?: boolean
  autoComplete?: string
  variant?: 'default' | 'material'
}

type InputState = {
  type: string
  setType: (type: string) => void
}
const useInputType = create<InputState>((set) => ({
  type: 'password',
  setType: (type: string) => set({ type }),
}))

function Input(props: IInput): JSX.Element {
  const {
    type,
    placeholder,
    name,
    label,
    defaultValue,
    control,
    inverted,
    rules,
    info,
    disabled,
    onChange,
    autoComplete,
    variant = 'default',
  } = props

  const { type: inputType, setType } = useInputType()
  const inputClassNames = classNames(
    'w-full py-2 text-gray-primary leading-tight focus:outline-none focus:shadow-outline',
    {
      'bg-gray-100': inverted && variant === 'default',
      'bg-white': !inverted && variant === 'default',
      'border border-gray-100': variant === 'default',
      'border-b-2 bg-white text-3xl font-bold': variant === 'material',
      'px-3 rounded': variant === 'default',
    },
  )
  const inputRef = React.useRef<HTMLInputElement>(null)

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field: { onChange: fieldOnChange, name, value },
        fieldState: { error },
      }) => (
        <div className="mb-4 w-full relative">
          {label && (
            <label
              className={`block text-blue-primary text-sm font-Spartan font-bold mb-2 ${
                rules?.required ? 'required' : ''
              }`}
              htmlFor={name}>
              {label}
            </label>
          )}
          <input
            autoComplete={autoComplete}
            ref={inputRef}
            className={classNames(`${inputClassNames}`, {
              'border-secondary-bittersweet-red': error,
            })}
            name={name}
            type={type === 'password' ? inputType : type}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value
              onChange?.(value)
              fieldOnChange(value)
            }}
            defaultValue={defaultValue}
            value={value}
          />
          {type === 'password' && (
            <span
              className={'absolute float-right right-2 translate-y-2 z-10 cursor-pointer'}
              onClick={(e) => {
                e.preventDefault()
                setType(inputType === 'password' ? 'text' : 'password')
              }}>
              {inputType === 'password' ? <EyeClosed /> : <EyeOpen />}
            </span>
          )}
          {error && (
            <span
              className={
                'font-Spartan font-medium text-xs text-secondary-bittersweet-red'
              }>
              {error.message}
            </span>
          )}
          {info && (
            <div className={'font-Spartan font-medium mt-2 text-xs text-web-gray'}>
              {info}
            </div>
          )}
        </div>
      )}
    />
  )
}

Input.defaultProps = {
  type: 'text',
} as Partial<IInput>

export default Input
